// src/lib/noteRules.js
//
// Reglas de notas informativas RECURRENTES, con historial versionado por
// identidad estable del perro (dog.recurringNotes). Funciones puras, sin
// dependencias circulares: solo importan helpers de fecha ya existentes.
//
// Modelo de datos (dog.recurringNotes), espejado también en templateDogs:
//   recurringNotes: [
//     { text, days, startDateKey, endDateKey }
//   ]
//   - days: mismo formato que schedules.days (weekday interno: 'mon'..'sun')
//   - startDateKey / endDateKey: "YYYY-MM-DD", comparables como string
//   - endDateKey === null (o undefined) => versión ABIERTA (vigente).
//     En otro caso, CERRADA: endDateKey es el ÚLTIMO día de vigencia
//     (inclusive).
//
// Invariante: como máximo una versión con endDateKey === null en cualquier
// momento dado. El histórico nunca se borra, solo se cierra.
//
// `dog.informativeNote` (string) sigue siendo el valor YA RESUELTO para un
// día concreto — estas funciones sirven para (re)calcularlo, nunca cambian
// su tipo ni su nombre. `dog.informativeNoteOverride` (boolean) marca si ese
// valor fue fijado deliberadamente para esa fecha exacta (modo puntual /
// "Solo esta semana"), protegiéndolo de recálculos futuros de la regla.

import { getWeekdayFromDateKey, subDaysToDateKey } from "@/lib/dateUtils";

/**
 * Resuelve el texto de nota aplicable para una fecha concreta, según el
 * historial de reglas recurrentes del perro. Devuelve "" si ninguna regla
 * cubre esa fecha para ese día de la semana.
 *
 * @param {Array} recurringNotes
 * @param {string} dateKey - "YYYY-MM-DD"
 * @returns {string}
 */
export function resolveNoteForDate(recurringNotes, dateKey) {
  if (!Array.isArray(recurringNotes) || !dateKey) return "";
  const weekday = getWeekdayFromDateKey(dateKey);
  const rule = recurringNotes.find(
    (r) =>
      r &&
      Array.isArray(r.days) &&
      r.days.includes(weekday) &&
      typeof r.startDateKey === "string" &&
      dateKey >= r.startDateKey &&
      (r.endDateKey === null || r.endDateKey === undefined || dateKey <= r.endDateKey)
  );
  return rule ? rule.text || "" : "";
}

/**
 * Indica si alguna fecha de `weekDateKeys` está cubierta por la VIGENCIA de
 * alguna regla del historial (abierta o cerrada), independientemente de si
 * esa fecha coincide con los `days` de la regla. Se usa para decidir si el
 * Switch "Solo esta semana" debe iniciarse desactivado al abrir el modal en
 * una semana que pertenece a un régimen recurrente, incluso si esa regla ya
 * está cerrada (histórica).
 *
 * @param {Array} recurringNotes
 * @param {string[]} weekDateKeys
 * @returns {boolean}
 */
export function weekIsUnderRecurringRegime(recurringNotes, weekDateKeys) {
  if (!Array.isArray(recurringNotes) || !Array.isArray(weekDateKeys)) return false;
  return recurringNotes.some(
    (rule) =>
      rule &&
      typeof rule.startDateKey === "string" &&
      weekDateKeys.some(
        (dk) =>
          dk >= rule.startDateKey &&
          (rule.endDateKey === null || rule.endDateKey === undefined || dk <= rule.endDateKey)
      )
  );
}

/**
 * Revisa el historial de reglas aplicando un cambio desde `changeDateKey`
 * (lunes de la semana editada) en adelante, de forma cronológicamente segura,
 * sin asumir que solo existe una única versión abierta ni que la edición
 * siempre ocurre después del inicio de esa versión:
 *
 * - Versiones TOTALMENTE ANTERIORES a changeDateKey (cerradas con
 *   endDateKey < changeDateKey) se conservan intactas.
 * - Versiones que ATRAVIESAN changeDateKey (startDateKey < changeDateKey y
 *   vigentes en o después de changeDateKey) se CIERRAN el día anterior a
 *   changeDateKey.
 * - Versiones cuyo startDateKey >= changeDateKey se DESCARTAN por completo
 *   (quedan reemplazadas uniformemente por el nuevo régimen desde
 *   changeDateKey).
 * - Si `newRules` se proporciona (uno o varios objetos { text, days }), se
 *   añade una nueva versión ABIERTA por cada grupo desde changeDateKey (esto
 *   permite que distintos días de la misma semana tengan textos distintos,
 *   igual que la UI permite escribir notas diferentes por día). Si es null/
 *   undefined/[] (terminación de la recurrencia, o ningún día con texto), no
 *   se añade ninguna versión nueva: el array queda solo con el histórico
 *   conservado (cierra cualquier regla vigente sin sustituirla).
 *
 * Garantiza: nunca se genera endDateKey < startDateKey. Pueden coexistir
 * varias versiones abiertas (endDateKey === null) simultáneamente SOLO SI
 * cubren días de la semana distintos entre sí (cada weekday, a su vez,
 * pertenece como máximo a una única versión abierta).
 *
 * @param {Array} existingRules
 * @param {string} changeDateKey - "YYYY-MM-DD", lunes de la semana editada
 * @param {{ text: string, days: string[] } | { text: string, days: string[] }[] | null} newRules
 * @returns {Array}
 */
/**
 * Determina si una excepción puntual (informativeNoteOverride === true)
 * debe permanecer protegida frente a un recálculo de recurrencia.
 * Una excepción puntual puede contener texto o estar intencionadamente vacía
 * para representar "sin nota solo esta semana".
 *
 * @param {{ informativeNoteOverride?: boolean, informativeNote?: string } | undefined} entry
 * @returns {boolean}
 */
export function isProtectedNoteException(entry) {
  return !!entry
    && entry.informativeNoteOverride === true
    && typeof entry.informativeNote === "string";
}

export function reviseRecurringNotes(existingRules, changeDateKey, newRules, affectedWeekdays) {


  const dayBeforeChange = subDaysToDateKey(changeDateKey, 1);
  const kept = [];
  
  // Si no se proporciona affectedWeekdays, todos los weekdays están afectados (comportamiento anterior)
  const isAffected = (wd) => !affectedWeekdays || affectedWeekdays.has(wd);

  for (const rule of existingRules || []) {
    if (!rule || typeof rule.startDateKey !== "string") continue;
    const isOpen = rule.endDateKey === null || rule.endDateKey === undefined;
    const closedBeforeChange = !isOpen && rule.endDateKey < changeDateKey;

    if (closedBeforeChange) {
      // Totalmente anterior al cambio: se conserva intacta.
      kept.push(rule);
      continue;
    }

    const ruleDays = Array.isArray(rule.days) ? rule.days : [];
    const affectedDaysInRule = ruleDays.filter(isAffected);
    const untouchedDaysInRule = ruleDays.filter(d => !isAffected(d));

    if (affectedDaysInRule.length === 0) {
      // Ningún weekday de esta regla cambia: se conserva intacta.
      kept.push(rule);
      continue;
    }

    if (untouchedDaysInRule.length > 0) {
      // Regla mixta: la porción NO afectada se separa y se preserva exactamente igual
      kept.push({ ...rule, days: [...untouchedDaysInRule] });
    }

    // Porción afectada: misma lógica de cierre/descarte
    const startsBeforeChange = rule.startDateKey < changeDateKey;
    if (startsBeforeChange) {
      // Atraviesa la fecha de cambio: se cierra la porción afectada en dayBeforeChange.
      kept.push({ ...rule, days: [...affectedDaysInRule], endDateKey: dayBeforeChange });
    }
    // rule.startDateKey >= changeDateKey → se descarta la porción afectada (sin reemplazante)
  }

  // Normalizar newRules a array: acepta un único objeto { text, days } o un
  // array de varios (distintos días con distintos textos en la misma semana).
  const rulesArray = Array.isArray(newRules)
    ? newRules
    : newRules
    ? [newRules]
    : [];

  rulesArray.forEach((rule) => {
    if (rule && Array.isArray(rule.days) && rule.days.length > 0) {
      kept.push({
        text: rule.text || "",
        days: [...rule.days],
        startDateKey: changeDateKey,
        endDateKey: null,
      });
    }
  });
  // Si newRules es null/vacío/[] (terminación), no se añade ninguna versión
  // nueva: el array queda solo con las versiones históricas conservadas.

  return kept;
}
